import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BellRing, CalendarClock, CheckCircle2, FileSearch, Loader2, RefreshCw, Settings2, ShieldOff, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import VehicleReviewDialog from "@/components/admin/VehicleReviewDialog";
import FrDateField from "@/components/FrDateField";

const BUCKETS = [
  { key: "d30", label: "≤ 30 jours" }, { key: "d15", label: "≤ 15 jours" }, { key: "d7", label: "≤ 7 jours" },
  { key: "expired", label: "Expirés" }, { key: "awaiting", label: "En attente de renouvellement" },
  { key: "pending", label: "À valider" }, { key: "blocked", label: "Conducteurs suspendus" },
];
const TONE = { valid: "bg-emerald-100 text-emerald-900", renewal_soon: "bg-amber-100 text-amber-900", expired: "bg-red-200 text-red-900",
  rejected: "bg-red-200 text-red-900", awaiting_renewal: "bg-sky-100 text-sky-900", pending: "bg-violet-100 text-violet-900" };
const KIND_FR = { id_recto: "Identité recto", id_verso: "Identité verso", driving_license_recto: "Permis recto", driving_license_verso: "Permis verso", carte_grise: "Carte grise", insurance: "Assurance" };
const GROUP_FR = { identity: "Identité", license: "Permis", carte_grise: "Carte grise", insurance: "Assurance" };
const KINDS = { identity: ["id_recto", "id_verso"], license: ["driving_license_recto", "driving_license_verso"], carte_grise: ["carte_grise"], insurance: ["insurance"] };

const filterRows = (data, b) => {
  if (!data) return [];
  if (b === "pending") return data.pending;
  if (b === "blocked") return data.blocked.map((x) => ({ ...x, group: "blocked", label: "Accès conducteur", status: "expired", status_label: "Suspendu" }));
  const n = { d30: 30, d15: 15, d7: 7 }[b];
  if (n) return data.rows.filter((r) => r.days_left !== null && r.days_left >= 0 && r.days_left <= n);
  return data.rows.filter((r) => r.status === (b === "expired" ? "expired" : "awaiting_renewal"));
};

export default function DocsRenewalTab() {
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState("d30");
  const [open, setOpen] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);

  const [lastRun, setLastRun] = useState(undefined);
  const load = useCallback(() => {
    api.get("/admin/price/docs/cron-runs").then(({ data: d }) => setLastRun(d.items[0] || null)).catch(() => {});
    return api.get("/admin/price/docs/overview").then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const rows = filterRows(data, bucket);
  const c = data?.counters || {};

  const runCheck = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/admin/price/docs/run-check"); toast.success(`Vérification faite — ${r.notifications} rappel(s) envoyé(s)`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const bulk = async () => {
    const ids = [...new Set(rows.map((r) => r.user_id))];
    if (!ids.length) return;
    try { const { data: r } = await api.post("/admin/price/docs/remind-bulk", { user_ids: ids }); toast.success(`${r.sent} rappel(s) envoyé(s)`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="docs-renewal-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-[Outfit] text-2xl font-black">Documents à renouveler</h2>
        <div className="flex flex-wrap gap-2">
          <Btn onClick={runCheck} testid="docs-run-check" icon={busy ? Loader2 : RefreshCw} spin={busy}>Lancer la vérification</Btn>
          <Btn onClick={() => setShowHistory(true)} testid="docs-open-history" icon={BellRing}>Historique</Btn>
          <Btn onClick={() => setShowSettings(true)} testid="docs-open-settings" icon={Settings2}>Paramètres</Btn>
        </div>
      </div>

      <LastRun run={lastRun} />

      <div className="grid gap-2 sm:grid-cols-3" data-testid="docs-counters">
        <Counter tone="bg-amber-100" testid="docs-counter-d30" text={`${c.d30 ?? 0} document(s) arrivent à expiration dans les 30 prochains jours`} />
        <Counter tone="bg-red-200" testid="docs-counter-expired" text={`${c.expired ?? 0} document(s) expiré(s) · ${c.blocked ?? 0} conducteur(s) suspendu(s)`} />
        <Counter tone="bg-violet-100" testid="docs-counter-pending" text={`${c.pending ?? 0} document(s) attendent ta validation`} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {BUCKETS.map((b) => (
          <button key={b.key} type="button" onClick={() => setBucket(b.key)} data-testid={`docs-bucket-${b.key}`}
            className={`rounded-full px-3 py-1.5 text-[12px] font-black street-border-thin ${bucket === b.key ? "bg-primary text-primary-foreground" : "bg-white"}`}>
            {b.label} ({c[b.key] ?? 0})
          </button>
        ))}
        {rows.length > 0 && bucket !== "pending" && <Btn onClick={bulk} testid="docs-remind-bulk" icon={BellRing}>Rappel groupé ({new Set(rows.map((r) => r.user_id)).size})</Btn>}
      </div>

      {!data ? <Loader2 className="h-6 w-6 animate-spin" /> : rows.length === 0 ? (
        <p className="rounded-2xl bg-white p-4 text-sm font-semibold street-border-thin" data-testid="docs-empty">Rien dans cette rubrique.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => <DocRow key={`${r.user_id}-${r.group}-${r.vehicle_id || i}`} r={r} onOpen={() => setOpen(r)} />)}
        </div>
      )}

      {open && open.group === "vehicle" && <VehicleReviewDialog row={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />}
      {open && open.group !== "vehicle" && <DossierDialog row={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />}
      {showSettings && <SettingsDialog onClose={() => setShowSettings(false)} />}
      {showHistory && <HistoryDialog onClose={() => setShowHistory(false)} />}
    </div>
  );
}

function Btn({ onClick, testid, icon: Icon, spin, children, tone = "bg-white" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-black street-border-thin street-press ${tone}`}>
      <Icon className={`h-3.5 w-3.5 ${spin ? "animate-spin" : ""}`} strokeWidth={2.75} />{children}
    </button>
  );
}

function Counter({ tone, text, testid }) {
  return <div className={`rounded-2xl p-3 text-[13px] font-black street-border-thin ${tone}`} data-testid={testid}>{text}</div>;
}

function DocRow({ r, onOpen }) {
  const urgent = r.status === "expired" || (r.days_left !== null && r.days_left !== undefined && r.days_left <= 7);
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-2xl bg-white p-3 street-border-thin ${urgent ? "ring-2 ring-red-500" : ""}`} data-testid={`docs-row-${r.user_id}-${r.group}`}>
      {urgent && <AlertTriangle className="h-5 w-5 text-red-600" />}
      <div className="min-w-[180px] flex-1">
        <div className="font-[Outfit] text-[15px] font-black">{r.name}</div>
        <div className="text-[12px] font-semibold text-foreground/60">{r.label}{r.vehicle ? ` · ${r.vehicle}` : ""}</div>
        {r.reason && <div className="text-[12px] font-bold text-red-700">{r.reason}</div>}
      </div>
      <div className="text-[12px] font-bold">
        {r.due ? <>Échéance {new Date(`${r.due}T12:00:00`).toLocaleDateString("fr-FR")}<br />{r.days_left < 0 ? `dépassée de ${-r.days_left} j` : `${r.days_left} j restants`}</> : "—"}
        {r.expiry_unknown && <div className="text-[11px] text-amber-700">date d&apos;expiration à renseigner</div>}
      </div>
      <span className={`rounded-full px-2.5 py-1 text-[11px] font-black ${TONE[r.status] || "bg-muted"}`}>{r.status_label || r.status}</span>
      <Btn onClick={onOpen} testid={`docs-open-${r.user_id}-${r.group}`} icon={FileSearch} tone="bg-accent">Dossier</Btn>
    </div>
  );
}

function DossierDialog({ row, onClose, onDone }) {
  const [group, setGroup] = useState(KINDS[row.group] ? row.group : "insurance");
  const [form, setForm] = useState({ issued_at: "", expires_at: "", next_check_at: "" });
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const call = async (path, body, msg) => {
    try { await api.post(`/admin/price/docs/${row.user_id}/${path}`, body); toast.success(msg); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const view = async (kind, renewal) => {
    try { const { data } = await api.get(`/admin/price/docs/${row.user_id}/file/${kind}`, { params: { renewal } }); setPreview(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 p-4" data-testid="docs-dossier-dialog">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-5 street-border street-shadow">
        <div className="flex items-start justify-between gap-2">
          <div><div className="font-[Outfit] text-xl font-black">{row.name}</div><div className="text-[12px] font-semibold text-foreground/60">{row.email}{row.vehicle ? ` · ${row.vehicle}` : ""}</div></div>
          <button type="button" onClick={onClose} data-testid="docs-dossier-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {Object.keys(KINDS).map((g) => (
            <button key={g} type="button" onClick={() => setGroup(g)} data-testid={`docs-dossier-group-${g}`}
              className={`rounded-full px-3 py-1 text-[12px] font-black street-border-thin ${group === g ? "bg-primary text-primary-foreground" : "bg-white"}`}>{GROUP_FR[g]}</button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {KINDS[group].map((k) => (
            <React.Fragment key={k}>
              <Btn onClick={() => view(k, false)} testid={`docs-view-${k}`} icon={FileSearch}>Voir {KIND_FR[k]}</Btn>
              {row.renewal_files?.includes(k) && <Btn onClick={() => view(k, true)} testid={`docs-view-new-${k}`} icon={FileSearch} tone="bg-sky-100">Nouveau : {KIND_FR[k]}</Btn>}
            </React.Fragment>
          ))}
        </div>
        {preview && (preview.data ? (
          preview.mime_type === "application/pdf"
            ? <a className="mt-2 block text-sm font-black underline" href={`data:application/pdf;base64,${preview.data}`} download={preview.filename}>Télécharger le PDF</a>
            : <img alt="document" className="mt-2 max-h-64 rounded-xl street-border-thin" src={`data:${preview.mime_type};base64,${preview.data}`} />
        ) : <p className="mt-2 text-sm text-red-700">{preview.error || "Indisponible"}</p>)}

        <div className="mt-4 grid grid-cols-3 gap-2">
          {[["issued_at", "Délivrance"], ["expires_at", "Expiration"], ["next_check_at", "Prochain contrôle"]].map(([k, l]) => (
            <label key={k} className="text-[11px] font-black uppercase">{l}
              <div className="mt-1"><FrDateField value={form[k]} onChange={(v) => setForm({ ...form, [k]: v })} testid={`docs-input-${k}`} /></div>
            </label>
          ))}
        </div>
        <p className="mt-1 text-[11px] font-semibold text-foreground/60">Assurance : sans date saisie, prochain contrôle = validation + 12 mois. La date de délivrance n&apos;est jamais utilisée comme expiration.</p>
        <button type="button" onClick={() => call("validate", { group, ...Object.fromEntries(Object.entries(form).filter(([, v]) => v)) }, "Document validé")}
          className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-emerald-500 font-black text-white street-border" data-testid="docs-validate">
          <CheckCircle2 className="h-4 w-4" />Valider ce document
        </button>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif (refus, justificatif complémentaire ou rappel)"
          className="mt-3 h-20 w-full rounded-xl p-2 text-[13px] street-border-thin" data-testid="docs-reason" />
        <div className="mt-2 grid grid-cols-3 gap-2">
          <Btn onClick={() => call("request-renewal", { group, reason }, "Justificatif demandé")} testid="docs-request" icon={CalendarClock}>Demander un justificatif</Btn>
          <Btn onClick={() => call("reject", { group, reason }, "Document refusé")} testid="docs-reject" icon={ShieldOff} tone="bg-red-100">Refuser</Btn>
          <Btn onClick={() => call("remind", { group }, "Rappel envoyé")} testid="docs-remind" icon={BellRing}>Envoyer un rappel</Btn>
        </div>
      </div>
    </div>
  );
}

function SettingsDialog({ onClose }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/price/docs/settings").then(({ data }) => setS(data)); }, []);
  const save = async () => {
    try {
      await api.put("/admin/price/docs/settings", {
        thresholds: String(s.thresholds).split(/[ ,;]+/).map(Number).filter((n) => !Number.isNaN(n)),
        email_enabled: s.email_enabled, admin_emails: String(s.admin_emails || "").split(/[ ,;]+/).filter(Boolean),
        priority_groups: s.priority_groups, insurance_months: Number(s.insurance_months) || 12,
      });
      toast.success("Paramètres enregistrés"); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 p-4" data-testid="docs-settings-dialog">
      <div className="w-full max-w-md rounded-3xl bg-white p-5 street-border street-shadow">
        <div className="flex justify-between"><div className="font-[Outfit] text-xl font-black">Paramètres des notifications</div><button type="button" onClick={onClose}><X className="h-5 w-5" /></button></div>
        {!s ? <Loader2 className="mt-4 h-5 w-5 animate-spin" /> : (
          <div className="mt-4 space-y-3 text-[13px] font-bold">
            <label className="block">Délais de rappel (jours avant échéance)
              <input value={String(s.thresholds)} onChange={(e) => setS({ ...s, thresholds: e.target.value })} className="mt-1 h-10 w-full rounded-xl px-2 street-border-thin" data-testid="docs-settings-thresholds" /></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!s.email_enabled} onChange={(e) => setS({ ...s, email_enabled: e.target.checked })} data-testid="docs-settings-email" />Envoyer aussi les rappels aux conducteurs par e-mail</label>
            <p className="text-[11px] font-semibold text-foreground/60">Les alertes administrateur arrivent en notification dans l&apos;application et dans Admin → Historique (plus d&apos;e-mail).</p>
            <label className="block">Renouvellement de l&apos;assurance (mois)
              <input type="number" value={s.insurance_months} onChange={(e) => setS({ ...s, insurance_months: e.target.value })} className="mt-1 h-10 w-full rounded-xl px-2 street-border-thin" data-testid="docs-settings-insurance" /></label>
            <div>Documents prioritaires
              <div className="mt-1 flex flex-wrap gap-2">{Object.keys(KINDS).map((g) => (
                <label key={g} className="flex items-center gap-1"><input type="checkbox" checked={s.priority_groups.includes(g)} data-testid={`docs-settings-prio-${g}`}
                  onChange={(e) => setS({ ...s, priority_groups: e.target.checked ? [...s.priority_groups, g] : s.priority_groups.filter((x) => x !== g) })} />{GROUP_FR[g]}</label>))}</div>
            </div>
            <button type="button" onClick={save} className="h-11 w-full rounded-full bg-primary font-black text-primary-foreground street-border" data-testid="docs-settings-save">Enregistrer</button>
          </div>
        )}
      </div>
    </div>
  );
}

function HistoryDialog({ onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/price/docs/notifications").then(({ data }) => setItems(data.items)); }, []);
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 p-4" data-testid="docs-history-dialog">
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-5 street-border street-shadow">
        <div className="flex justify-between"><div className="font-[Outfit] text-xl font-black">Historique des notifications</div><button type="button" onClick={onClose}><X className="h-5 w-5" /></button></div>
        {!items ? <Loader2 className="mt-4 h-5 w-5 animate-spin" /> : items.length === 0 ? <p className="mt-4 text-sm">Aucune notification envoyée.</p> : (
          <table className="mt-3 w-full text-left text-[12px]"><thead><tr className="font-black"><th>Date</th><th>Destinataire</th><th>Type</th><th>Document</th><th>Statut</th></tr></thead>
            <tbody>{items.map((n) => (<tr key={n.id} className="border-t"><td>{new Date(n.sent_at).toLocaleString("fr-FR")}</td><td>{n.recipient}</td><td>{n.type}</td><td>{GROUP_FR[n.group] || "—"}</td><td>{n.status}</td></tr>))}</tbody></table>
        )}
      </div>
    </div>
  );
}

const RUN_FR = { running: "en cours", done: "réussie", partial: "terminée avec erreurs", failed: "en échec" };
function LastRun({ run }) {
  if (run === undefined) return <p className="text-[12px] font-semibold text-foreground/40" data-testid="docs-last-run">Chargement…</p>;
  if (!run) return <p className="text-[12px] font-semibold text-foreground/60" data-testid="docs-last-run">Aucune vérification enregistrée.</p>;
  const bad = run.status === "failed" || run.status === "partial";
  return (
    <p className={`rounded-xl px-3 py-2 text-[12px] font-bold street-border-thin ${bad ? "bg-red-100 text-red-900" : "bg-white"}`} data-testid="docs-last-run">
      Dernière vérification ({run.source === "admin" ? "manuelle" : "automatique"}) : {new Date(run.at).toLocaleString("fr-FR")} — {RUN_FR[run.status] || "ancienne exécution"}
      {run.result ? ` · ${run.result.drivers} conducteur(s), ${run.result.notifications} rappel(s)` : ""}
      {run.error ? ` · ${run.error}` : ""}{run.result?.errors?.length ? ` · ${run.result.errors.length} erreur(s)` : ""}
    </p>
  );
}
